import { c as _c } from "react/compiler-runtime";
import ParticipationIdentityProfileCard, { type ParticipationIdentityProfileCardVariant } from "@/components/waves/drops/participation/ParticipationIdentityProfileCard";
import type { ApiDropMetadataResponse } from "@/generated/models/ApiDropMetadataResponse";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
import Link from "next/link";
import { getDropIdentityFallbackValue, getDropIdentityProfile } from "../identityDisplay.helpers";
export type WaveDropIdentityVariant = "full" | "compact";
interface WaveDropIdentityProps {
  readonly drop: {
    readonly id?: string | number | undefined;
    readonly wave: Pick<ApiWaveMin, "submission_type"> | null | undefined;
    readonly metadata: readonly ApiDropMetadataResponse[] | null | undefined;
  };
  readonly variant: WaveDropIdentityVariant;
  readonly cardVariant?: ParticipationIdentityProfileCardVariant | undefined;
  readonly className?: string | undefined;
  readonly testIdPrefix?: string | undefined;
}
const getIdentityHref = (value: string) => `/${encodeURIComponent(value.toLowerCase())}`;
function IdentityFallbackCard(t0) {
  const $ = _c(6);
  const {
    fallbackValue,
    testId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-[0.14em] tw-text-iron-500">Identity</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== fallbackValue) {
    t2 = <p className="tw-mb-0 tw-mt-3 tw-break-all tw-text-sm tw-font-semibold tw-text-iron-50">{fallbackValue}</p>;
    $[1] = fallbackValue;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2 || $[4] !== testId) {
    t3 = <div data-testid={testId} className="tw-rounded-xl tw-border tw-border-iron-800/60 tw-bg-iron-900/50 tw-p-4">{t1}{t2}</div>;
    $[3] = t2;
    $[4] = testId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function WaveDropIdentity(t0) {
  const $ = _c(25);
  const {
    drop,
    variant,
    cardVariant,
    className,
    testIdPrefix: t1
  } = t0;
  const testIdPrefix = t1 === undefined ? "wave-drop-identity" : t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== cardVariant || $[1] !== className || $[2] !== drop.id || $[3] !== drop.metadata || $[4] !== drop.wave || $[5] !== testIdPrefix || $[6] !== variant) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const identityProfile = getDropIdentityProfile({
        wave: drop.wave,
        metadata: drop.metadata
      });
      const fallbackValue = identityProfile ? null : getDropIdentityFallbackValue({
        wave: drop.wave,
        metadata: drop.metadata
      });
      if (!identityProfile && !fallbackValue) {
        t7 = null;
        break bb0;
      }
      if (variant === "full") {
        const t8 = identityProfile ? <div data-testid={`${testIdPrefix}-full`}><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant={cardVariant} /></div> : <IdentityFallbackCard fallbackValue={fallbackValue} testId={`${testIdPrefix}-full`} />;
        let t9;
        if ($[13] !== className || $[14] !== t8) {
          t9 = <div className={className}>{t8}</div>;
          $[13] = className;
          $[14] = t8;
          $[15] = t9;
        } else {
          t9 = $[15];
        }
        t7 = t9;
        break bb0;
      }
      const displayLabel = identityProfile?.handle ?? identityProfile?.primary_address ?? fallbackValue;
      if (!displayLabel) {
        t7 = null;
        break bb0;
      }
      t6 = className;
      t2 = `${testIdPrefix}-compact`;
      t3 = "tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-iron-800/50 tw-bg-iron-900/40 tw-px-2.5 tw-py-2";
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <span className="tw-flex-shrink-0 tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-[0.14em] tw-text-iron-500">Identity</span>;
        $[16] = t4;
      } else {
        t4 = $[16];
      }
      t5 = identityProfile ? <Link href={getIdentityHref(displayLabel)} prefetch={false} onClick={_temp} className="tw-min-w-0 tw-truncate tw-text-sm tw-font-semibold tw-text-iron-100 tw-no-underline tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-iron-300">{displayLabel}</Link> : <span className="tw-min-w-0 tw-break-all tw-text-sm tw-font-semibold tw-text-iron-100">{displayLabel}</span>;
    }
    $[0] = cardVariant;
    $[1] = className;
    $[2] = drop.id;
    $[3] = drop.metadata;
    $[4] = drop.wave;
    $[5] = testIdPrefix;
    $[6] = variant;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5) {
    t8 = <div data-testid={t2} className={t3}>{t4}{t5}</div>;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t6 || $[23] !== t8) {
    t9 = <div className={t6}>{t8}</div>;
    $[22] = t6;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
function _temp(event) {
  return event.stopPropagation();
}